import { LIMITS } from './constants';
import { disciplinesFor, impliedSex } from './disciplines';
import { t } from '../i18n';
import type {
  AgeTier,
  Athlete,
  Club,
  Competition,
  Entry,
  Exercise,
  FigureVariant,
  Programme,
  Workspace,
  Sex,
  Starter,
  StarterType,
} from '../types';

export function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function emptyWorkspace(): Workspace {
  return {
    version: 1,
    meta: { club: '', lv: 'Hessen', federation: '' },
    contact: {
      firstName: '', lastName: '', uciId: '', countryCode: '', postalCode: '',
      city: '', street: '', phone: '', fax: '', email: '',
    },
    comps: [],
    clubs: [],
    athletes: [],
    starters: [],
    programs: [],
    entries: [],
    activeProgramId: null,
  };
}

export function newClub(): Club {
  return {
    id: uid(),
    name: '',
    nation: 'GER',
    federation: '',
    number: '',
    contact: {
      firstName: '', lastName: '', street: '', postalCode: '', city: '',
      phone: '', fax: '', email: '',
    },
  };
}

export function newAthlete(defaultClubId = ''): Athlete {
  return {
    id: uid(),
    firstName: '', lastName: '', uciId: '', birthDate: '',
    sex: 'w', nation: 'GER', clubId: defaultClubId,
    countryCode: '', postalCode: '', city: '', street: '', phone: '', fax: '', email: '',
  };
}

export function newComp(): Competition {
  return { id: uid(), name: '', date: '', venue: '', organiser: '' };
}

export function newStarter(): Starter {
  const first = disciplinesFor('single')[0]!;
  return {
    id: uid(),
    type: 'single',
    discipline: first.de,
    disciplineEn: first.en,
    name: '',
    personalBest: { points: '', date: '', venue: '' },
    scoringReportEmail: '',
    athleteIds: [],
    clubAlias: '',
  };
}

export function newProgramme(type: StarterType, tier: AgeTier): Programme {
  return { id: uid(), type, ageTier: tier, name: '', program: [] };
}

export function newEntry(): Entry {
  return { id: uid(), compId: '', starterId: '', programId: '' };
}

/* ---------------------------------------------------------------- selectors */

export function athleteName(a: Athlete): string {
  const n = `${a.firstName} ${a.lastName}`.trim();
  return n.length > 0 ? n : t('athletes.unnamed');
}

/** `Startername`: the explicit name, otherwise the athletes' surnames. */
export function starterName(workspace: Workspace, s: Starter): string {
  if (s.name.trim().length > 0) return s.name.trim();
  const parts = s.athleteIds.map((id) => {
    const a = workspace.athletes.find((x) => x.id === id);
    if (!a) return t('common.unknownPlaceholder');
    return a.lastName.trim() || a.firstName.trim() || t('common.unknownPlaceholder');
  });
  return parts.length > 0 ? parts.join(' / ') : t('starter.unnamed');
}

export function athletesOf(workspace: Workspace, s: Starter): Athlete[] {
  return s.athleteIds
    .map((id) => workspace.athletes.find((a) => a.id === id))
    .filter((a): a is Athlete => a !== undefined);
}

/** The `Club` an athlete belongs to, resolved live by id — `undefined` when
 *  unset or (transiently, mid-edit) dangling. */
export function athleteClub(workspace: Workspace, a: Athlete): Club | undefined {
  return workspace.clubs.find((c) => c.id === a.clubId);
}

/** `Verein` on the athlete: the current name of the club it references, so a
 *  club rename is reflected everywhere immediately, including XML export. */
export function athleteClubName(workspace: Workspace, a: Athlete): string {
  return athleteClub(workspace, a)?.name ?? '';
}

/** A programme's display name: the given one, otherwise its type + tier. */
export function programmeLabel(p: Programme, typeLabel: string, tierLabel: string): string {
  if (p.name.trim().length > 0) return p.name.trim();
  return `${typeLabel} · ${tierLabel}`;
}

export interface ResolvedEntry {
  entry: Entry;
  comp: Competition;
  starter: Starter;
  programme: Programme;
}

/**
 * Every `Entry` entered in `compId`, with its starter and programme
 * resolved. An `Entry` whose starter or programme reference doesn't
 * resolve (mid-edit, or a stale reference) is skipped rather than crashing
 * — `validate()` reports the incomplete entry separately.
 */
export function entriesFor(workspace: Workspace, compId: string): ResolvedEntry[] {
  const comp = workspace.comps.find((c) => c.id === compId);
  if (!comp) return [];
  return workspace.entries
    .filter((e) => e.compId === compId)
    .map((e) => {
      const starter = workspace.starters.find((s) => s.id === e.starterId);
      const programme = workspace.programs.find((p) => p.id === e.programId);
      return starter && programme ? { entry: e, comp, starter, programme } : null;
    })
    .filter((x): x is ResolvedEntry => x !== null);
}

/**
 * `VereinStarter`/`VereinTeam`, `Istsportgemeinschaft`/`Sportgemeinschaft` and
 * `GeschlechtStarter` are not entered by hand — each is derived from the
 * assigned athletes' own club/sex, since re-typing what is already on the
 * athlete record would just be a second, driftable copy of the same fact.
 */

/** Distinct, non-empty club names among the assigned athletes, in the order
 *  the athletes were added. */
export function starterClubs(workspace: Workspace, s: Starter): string[] {
  const names = athletesOf(workspace, s)
    .map((a) => athleteClubName(workspace, a).trim())
    .filter((v) => v.length > 0);
  return [...new Set(names)];
}

/** The athletes' clubs joined for an SG, before any alias override — this is
 *  what can overrun the 40-character `VereinStarter`/`VereinTeam` limit. */
export function starterClubFull(workspace: Workspace, s: Starter): string {
  return starterClubs(workspace, s).join(' / ');
}

/** `VereinStarter`/`VereinTeam`: the athletes' clubs, combined for an SG —
 *  or `s.clubAlias` when the coach has set one, e.g. because the joined
 *  names overrun the 40-character XSD limit and there is no way to
 *  hand-abbreviate a name derived from the athletes' own club names.
 *
 *  With no alias, a name past the limit is truncated to fit — the same
 *  blind character cutoff WeBo2008-Qt itself applies (confirmed against a
 *  real export, see test/fixtures/authKliehm.ts), rather than blocking the
 *  export. An alias always wins over the auto-truncation, since it is the
 *  coach's explicit choice of a nicer abbreviation. */
export function starterClub(workspace: Workspace, s: Starter): string {
  const alias = s.clubAlias.trim();
  if (alias !== '') return alias;
  return starterClubFull(workspace, s).slice(0, LIMITS.club);
}

/** `Istsportgemeinschaft`/`Sportgemeinschaft`: true once athletes from more
 *  than one club are assigned. */
export function starterIsSG(workspace: Workspace, s: Starter): boolean {
  return starterClubs(workspace, s).length > 1;
}

/** `GeschlechtStarter`: the shared sex of the assigned athletes, or `m` when
 *  they are mixed or none are assigned yet — matching the disciplines list,
 *  where every non-"Frauen" class is simply "offen" rather than a distinct
 *  men's category. */
export function starterSex(workspace: Workspace, s: Starter): Sex {
  const sexes = new Set(athletesOf(workspace, s).map((a) => a.sex));
  return sexes.size === 1 ? [...sexes][0]! : 'm';
}

/** Assigned athletes whose own sex conflicts with a women's-only discipline
 *  class ("Frauen"/"Juniorinnen"/"Schülerinnen") — empty when the discipline
 *  doesn't restrict by sex, or every athlete matches. A women's category may
 *  only be started by women, in either direction: an all-male or mixed
 *  starter is not eligible, for any starter size including a 1er. */
export function sexMismatches(workspace: Workspace, s: Starter): Athlete[] {
  const required = impliedSex(s.discipline);
  if (required === null) return [];
  return athletesOf(workspace, s).filter((a) => a.sex !== required);
}

/** `Aufgestellt`: the sum of the announced difficulty points, read from each
 *  exercise's resolved catalogue variant (see `resolveProgram()` in
 *  `src/data/catalogue.ts`) — an unresolved figure (still loading, or a
 *  stale id) contributes nothing rather than throwing. */
export function totalPoints(resolved: ReadonlyArray<FigureVariant | undefined>): number {
  return Math.round(resolved.reduce((t, v) => t + (v?.points ?? 0), 0) * 100) / 100;
}

/** Figure numbers that appear more than once; a repeat scores nothing. */
export function duplicateNumbers(exercises: readonly Exercise[]): string[] {
  const seen = new Set<string>();
  const dup = new Set<string>();
  for (const u of exercises) {
    if (seen.has(u.nr)) dup.add(u.nr);
    seen.add(u.nr);
  }
  return [...dup];
}

/** Find a club by exact name in `clubs`, or mint and append a new one — used
 *  wherever a foreign source only carries a club by name rather than a real
 *  `Club.id` reference, i.e. the WeBo XML importer
 *  (`src/domain/importWebo.ts`). Mutates `clubs` in place, matching how the
 *  call site builds up a workspace's club list incrementally. */
export function findOrCreateClub(clubs: Club[], name: string): Club {
  const existing = clubs.find((c) => c.name === name);
  if (existing) return existing;
  const club = { ...newClub(), name };
  clubs.push(club);
  return club;
}

/** Drop references that no longer resolve, in place: an athlete's dangling
 *  `clubId` is cleared, a starter's `athleteIds` filtered, and an `Entry`
 *  with any dangling reference dropped outright — it has no meaning with
 *  only some of its three references intact, unlike a list of athletes.
 *  Shared by `parseWorkspace()` (below), `applyImport()`
 *  (`src/domain/importExport.ts`) and `applyWeboImport()`
 *  (`src/domain/importWebo.ts`), since all three build up a `Workspace` from
 *  parts that may not all resolve against each other. */
export function cleanupDanglingReferences(workspace: Workspace): void {
  const clubIds = new Set(workspace.clubs.map((c) => c.id));
  for (const a of workspace.athletes) {
    if (a.clubId !== '' && !clubIds.has(a.clubId)) a.clubId = '';
  }
  const athleteIds = new Set(workspace.athletes.map((a) => a.id));
  for (const s of workspace.starters) {
    s.athleteIds = s.athleteIds.filter((id) => athleteIds.has(id));
  }
  const compIds = new Set(workspace.comps.map((c) => c.id));
  const starterIds = new Set(workspace.starters.map((s) => s.id));
  const programIds = new Set(workspace.programs.map((p) => p.id));
  workspace.entries = workspace.entries.filter(
    (e) =>
      (e.compId === '' || compIds.has(e.compId)) &&
      (e.starterId === '' || starterIds.has(e.starterId)) &&
      (e.programId === '' || programIds.has(e.programId)),
  );
}

/**
 * Accept a workspace file without trusting it. Unknown or missing fields fall
 * back to the empty workspace so a hand-edited file still opens. No shape
 * migration: only the current `Workspace` shape is read. The app has never
 * been published, so there is no old-format data to carry forward — the
 * only external compatibility this codebase maintains is the WeBo XML
 * import/export and the two XSD schemas (see `src/xml/`).
 */
export function parseWorkspace(raw: unknown): Workspace {
  if (typeof raw !== 'object' || raw === null) throw new Error('Kein Projektobjekt.');
  const base = emptyWorkspace();
  const src = raw as Partial<Workspace>;

  const comps: Competition[] = Array.isArray(src.comps) ? src.comps.map((c) => ({ ...newComp(), ...c })) : [];

  const starters: Starter[] = Array.isArray(src.starters)
    ? src.starters.map((raw2) => {
        const fallback = newStarter();
        return {
          id: typeof raw2.id === 'string' ? raw2.id : fallback.id,
          type: raw2.type ?? fallback.type,
          discipline: raw2.discipline ?? fallback.discipline,
          disciplineEn: raw2.disciplineEn ?? fallback.disciplineEn,
          name: raw2.name ?? fallback.name,
          personalBest: {
            points: raw2.personalBest?.points ?? '',
            date: raw2.personalBest?.date ?? '',
            venue: raw2.personalBest?.venue ?? '',
          },
          scoringReportEmail: raw2.scoringReportEmail ?? fallback.scoringReportEmail,
          athleteIds: Array.isArray(raw2.athleteIds) ? [...raw2.athleteIds] : [],
          clubAlias: raw2.clubAlias ?? fallback.clubAlias,
        };
      })
    : [];

  const clubs: Club[] = Array.isArray(src.clubs)
    ? src.clubs.map((c) => ({
        ...newClub(),
        ...c,
        contact: { ...newClub().contact, ...c.contact },
      }))
    : [];

  const athletes: Athlete[] = Array.isArray(src.athletes) ? src.athletes.map((a) => ({ ...newAthlete(), ...a })) : [];

  const workspace: Workspace = {
    version: 1,
    meta: { ...base.meta, ...src.meta },
    contact: { ...base.contact, ...src.contact },
    comps,
    clubs,
    athletes,
    starters,
    programs: Array.isArray(src.programs)
      ? src.programs.map((p) => ({
          ...newProgramme('single', 'eliteJunior'),
          ...p,
          program: Array.isArray(p.program) ? p.program.map((u) => ({ nr: u?.nr ?? '' })).filter((u) => u.nr !== '') : [],
        }))
      : [],
    entries: Array.isArray(src.entries) ? src.entries.map((e) => ({ ...newEntry(), ...e })) : [],
    activeProgramId: typeof src.activeProgramId === 'string' ? src.activeProgramId : null,
  };

  cleanupDanglingReferences(workspace);

  return workspace;
}
